import type { ILink } from "@components/core/Link";
import type { INavigationItem, ISubNav, SubNavLink } from "@components/navigation/types";
import type {
  Config,
  FaqsRepeaterBlock,
  ICmsComplexTextField,
  ICmsCtaButton,
  ICmsLink,
  ICmsLinkGroup,
  ICmsNavigationLinks,
  Media,
  NarrativesBlock,
  Voice as PayloadVoice,
} from "@mw/shared/payload-types";
import type { Voice } from "@/app/_data/voices";
import type { ImageProps, StaticImageData } from "next/image";

import { env } from "@/env";
import { getTechnologyPageIcon, isPagesv2TechnologyPage } from "@/app/_utilities/technologyPage";
import { logger } from "@utilities/logger";
import { isNil } from "@ui/lib/utils";
import { nl2Br } from "@ui/lib/text";
export type CollectionKeys = keyof Config["collections"];
export type Collection = Config["collections"][CollectionKeys];
export type PayloadReturn = null | string | undefined;

export type ActionButton =
  | {
      type: "videoPopup";
      ariaLabel?: string;
      label: string;
      video: string;
    }
  | {
      type: "authDialog";
      tab: "sign-up" | "sign-in";
      ariaLabel?: string;
      label: string;
      redirectTo?: string;
    };

export type FormattedCTA =
  | {
      action: "link";
      link: NonNullable<ReturnType<typeof formatLink>>;
    }
  | {
      action: "button";
      button: ActionButton;
    };

const isEntity = <T extends Collection>(entity: PayloadReturn | T): entity is T => {
  return entity !== null && entity !== undefined && typeof entity === "object";
};

/*
 * Returns an array of entities that are published
 */
export const filterEntitiesArray = <T extends Collection>(
  entity: (T | string | undefined | null)[] | PayloadReturn,
): T[] => {
  return Array.isArray(entity) ? entity.filter((e): e is T => isEntity(e) && isPublished(e)) : [];
};

const isPublished = <T extends Collection>(entity: T): boolean => {
  return typeof entity === "object" && "_status" in entity && entity._status === "published";
};

export const formatLink = (link: ICmsLink | null | undefined): ILink | null => {
  if (!link) return null;

  let _url = link.url;
  const isExternal =
    (link.type === "custom" && !link.url?.startsWith("/") && !link.url?.startsWith(env.NEXT_PUBLIC_SERVER_URL)) ||
    link.newTab;
  const reference = link.type === "reference" ? link.reference : null;
  if (reference && isEntity(reference?.value)) {
    switch (reference?.relationTo) {
      // case "use-cases":
      case "pagesv2": {
        _url = `${reference?.value?.slug}`;
        break;
      }
      case "languages":
        _url = `text-to-speech/${reference?.value?.slug}`;
        break;
      case "use-cases":
        _url = `use-cases/${reference?.value?.slug}`;
        break;
      case "articles":
        _url = `blog/${reference?.value?.slug}`;
        break;
      case "creative-templates":
        _url = `${reference?.value?.slug}`;
        break;
    }
  }
  // Handles people typing the wrong urls or not https.
  if (!reference && _url) {
    // Only convert to relative path if it's the main domain (not a subdomain)
    if (_url.includes("elevenlabs.io/") && !_url.includes(".elevenlabs.io/")) {
      _url = _url.substring(_url.indexOf("elevenlabs.io/") + "elevenlabs.io/".length);
    } else if (_url.includes("eleven2.dev/") && !_url.includes(".eleven2.dev/")) {
      _url = _url.substring(_url.indexOf("eleven2.dev/") + "eleven2.dev/".length);
    }
  }

  let img = null;
  if (reference && isEntity(reference?.value)) {
    switch (reference?.relationTo) {
      case "pagesv2": {
        const icon = isPagesv2TechnologyPage(reference.value) ? getTechnologyPageIcon(reference.value) : null;
        img = icon ? { resource: icon } : null;
        break;
      }
    }
  }

  return !isNil(_url) && _url !== ""
    ? {
        href: _url as string,
        label: link.label?.trim() || undefined,
        ariaLabel: link.ariaLabel || undefined,
        target: isExternal ? "_blank" : undefined,
        rel: isExternal ? "noopener noreferrer" : undefined,
        appearance: link.appearance ?? undefined,
        img,
        iconSource: {
          media: link.iconConfig?.media ?? null,
        },
        copyUTMTags: link.copyUTMTags ?? false,
      }
    : null;
};

export const formatCTA = (cta: ICmsCtaButton | ICmsLink | null | undefined): FormattedCTA | null => {
  if (!cta || !cta.type || !cta.label || !cta.label.trim()) return null;

  switch (cta.type) {
    case "custom":
    case "reference": {
      const link = formatLink(cta as ICmsLink);
      // since this is a CTA, we actually don't want to render it if it doesn't have a label
      return link
        ? {
            action: "link",
            link: link,
          }
        : null;
    }

    case "button": {
      switch (cta.template?.template) {
        case "videoPopup": {
          const videoUrl = cta.template?.videoPopup?.video?.url;
          if (!videoUrl) return null;
          return {
            action: "button",
            button: {
              type: "videoPopup",
              label: cta.label,
              video: videoUrl,
              ariaLabel: cta.ariaLabel ?? "",
            },
          };
        }

        case "authDialog": {
          return {
            action: "button",
            button: {
              type: "authDialog",
              tab: cta.template?.authDialog?.tab ?? "sign-up",
              label: cta.label,
              ariaLabel: cta.ariaLabel ?? "",
              redirectTo: cta.template?.authDialog?.redirectTo ?? undefined,
            },
          };
        }

        default: {
          const _: never | undefined = cta.template?.template;
          logger.error(null, "[formatCTA] Tried to convert an invalid CTA", cta);
          return null;
        }
      }
    }
    default: {
      const _: never = cta.type;
      logger.error(null, "[formatCTA] Tried to convert an invalid CTA", cta);
      return null;
    }
  }
};

const resolvePayloadMediaUrl = (url: string) => {
  if (url.startsWith("/")) {
    return `${env.NEXT_PUBLIC_CMS_URL}${url}`;
  }
  return url;
};

export const getVideoUrl = (video: Media | PayloadReturn): string | undefined => {
  if (isEntity(video) && video.mimeType?.startsWith("video") && video.url) {
    return resolvePayloadMediaUrl(video.url);
  }

  return undefined;
};

export const getAudioUrl = (audio: Media | PayloadReturn): string | undefined => {
  if (isEntity(audio) && audio.mimeType?.startsWith("audio") && audio.url) {
    return resolvePayloadMediaUrl(audio.url);
  }

  return undefined;
};

export const getImageUrl = (image: Media | PayloadReturn): string | undefined => {
  if (isEntity(image) && image.mimeType?.startsWith("image") && image.url) {
    return resolvePayloadMediaUrl(image.url);
  }
  return undefined;
};

export const getImageProps = (
  resource: Media | string | null | undefined,
  { placeholder = false, fill = false }: { placeholder?: boolean; fill?: boolean } = {},
): ImageProps | null => {
  if (!resource) {
    return null;
  }

  const src = typeof resource === "string" ? resource : resource.url;

  if (!src) {
    return null;
  }

  const alt = typeof resource === "string" ? resource : (resource.alt ?? "");
  const width = typeof resource === "string" ? undefined : (resource.width ?? undefined);
  const height = typeof resource === "string" ? undefined : (resource.height ?? undefined);

  const placeholderProps =
    placeholder && typeof resource === "object" && resource.blurDataURL
      ? { placeholder: "blur" as const, blurDataURL: resource.blurDataURL }
      : {};

  const sizeProps = fill ? { fill: true } : { width, height };

  return {
    src,
    alt,
    ...sizeProps,
    ...placeholderProps,
  };
};

/**
 * Converts a Next.js StaticImageData to a Media-like object
 * for compatibility with CMS Media fields
 */
export const staticImageToMedia = (image: StaticImageData, alt?: string): Media => {
  return {
    id: `static-${image.src.split("/").pop()?.split(".")[0] ?? "image"}`,
    url: image.src,
    width: image.width,
    height: image.height,
    alt: alt ?? null,
    blurDataURL: image.blurDataURL ?? null,
    updatedAt: "",
    createdAt: "",
  };
};

export const getLinkProps = (link: ICmsLink | null | undefined) => {
  const formatted = formatLink(link);

  if (!formatted) {
    return null;
  }

  return {
    href: formatted.href,
    rel: formatted.rel,
    "aria-label": formatted.ariaLabel,
    target: formatted.target,
    children: formatted.label,
  };
};

const formatLinks = (links: ICmsLinkGroup | null | undefined): ILink[] => {
  if (!links) return [];
  return (
    links
      ?.filter(({ link }) => !isNil(link))
      .reduce((acc, { link, description }) => {
        const _link = formatLink(link);

        if (!_link) {
          return acc;
        }

        _link.description = description;

        acc.push(_link);

        return acc;
      }, [] as ILink[]) || []
  );
};

const formatNavLinks = (links: ICmsNavigationLinks): INavigationItem[] => {
  return (
    links.reduce((acc, { link, subNavs, featured, label, navigationType, rootLink }) => {
      if (navigationType === "link") {
        const _link = link && formatLink(link);

        if (!_link) {
          return acc;
        }

        acc.push({ ..._link });

        return acc;
      }

      const featureLink =
        featured?.map(({ link, ...rest }) => ({
          link: formatLink(link),
          ...rest,
        }))[0] || null;

      const formattedSubNavs =
        subNavs?.map(subNav => {
          return {
            label: subNav.label || "",
            layout: subNav.layout || "default",
            items: (subNav.links || []).reduce((links, { link, description, icon: customIcon }) => {
              const _link = formatLink(link);
              if (!_link) {
                return links;
              }

              const reference = link?.type === "reference" ? link?.reference : null;
              let icon = null;
              if (reference && isEntity(reference?.value)) {
                switch (reference?.relationTo) {
                  case "pagesv2": {
                    const technologyIcon = isPagesv2TechnologyPage(reference.value)
                      ? getTechnologyPageIcon(reference.value)
                      : null;
                    icon = technologyIcon ? { resource: technologyIcon } : null;
                    break;
                  }
                }
              }
              if (customIcon) {
                icon = {
                  resource: customIcon,
                };
              }

              links.push({
                ..._link,
                description,
                icon,
              });
              return links;
            }, [] as SubNavLink[]),
          };
        }) || [];

      acc.push({
        label: label || "",
        rootLink: rootLink && formatLink(rootLink),
        subNavs: formattedSubNavs,
        subNavColumns: balanceSubNavs(formattedSubNavs).length,
        featured: featureLink,
      });

      return acc;
    }, [] as INavigationItem[]) || []
  );
};

const programaticNarrativesToNarrative = (narratives: {
  cover?: Media | null | string;
  extraNarratives?: NarrativesBlock[];
  longDescription?: null | string;
  longTitle?: null | string;
}): NarrativesBlock | null => {
  const narrative: NarrativesBlock = {
    blockType: "narratives",
    alternateAlignment: true,
    narratives: [],
  };

  if (narratives.longTitle && narratives.longDescription && narratives.cover) {
    narrative.narratives!.push({
      assetType: "image",
      title: narratives.longTitle,
      description: narratives.longDescription,
      image: narratives.cover,
      backgroundColor: "#f0cd0c",
    });
  }

  if (narratives.extraNarratives && narratives.extraNarratives.length > 0) {
    const extra = narratives.extraNarratives
      .map(n => n.narratives)
      .filter(Boolean)
      .flat();
    narrative.narratives = narrative.narratives!.concat(extra as NonNullable<NarrativesBlock["narratives"]>);
  }

  return narrative.narratives?.length ? narrative : null;
};

const programmaticFaqsToFaqs = (
  faqConfig: {
    question?: { answer?: null | string; id?: null | string; question: string }[] | null;
    strategy: "extend" | "override";
  },
  categoryTitle: string,
  defaultFaqs: { answer: string | undefined; question: string }[],
): FaqsRepeaterBlock["question"] => {
  let faqs: FaqsRepeaterBlock["question"] = null;

  switch (faqConfig.strategy) {
    case "extend": {
      faqs = [...(faqConfig.question ?? []), ...defaultFaqs];
      break;
    }
    case "override":
      faqs = faqConfig.question;
      break;
  }

  return faqs;
};

export interface FormattedComplexTextField extends Required<ICmsComplexTextField> {
  options: Required<ICmsComplexTextField["options"]> & { tag: any };
}

const formatComplexTextField = (
  complexTextField: ICmsComplexTextField | null | undefined,
  {
    defaultTag = "p",
    defaultSize = "medium",
  }: {
    defaultTag?: NonNullable<ICmsComplexTextField["options"]>["tag"];
    defaultSize?: NonNullable<ICmsComplexTextField["options"]>["size"];
  },
): FormattedComplexTextField | null => {
  if (!complexTextField || !complexTextField.text) {
    return null;
  }
  return {
    text: nl2Br(complexTextField.text),
    options: {
      tag: complexTextField.options?.tag || defaultTag,
      size: complexTextField.options?.size || defaultSize,
    },
  };
};

/**
 * Transforms a Payload Voice object to the frontend Voice type.
 */
export function transformPayloadVoice(payloadVoice: PayloadVoice): Voice {
  return {
    voiceId: payloadVoice.voiceId,
    name: payloadVoice.name,
    image: payloadVoice.image,
    imageFace: payloadVoice.imageFace ?? undefined,
    tags: payloadVoice.tags?.map(t => ({ name: t.name ?? "" })) ?? [],
    category: payloadVoice.category ?? undefined,
    shortDescription: payloadVoice.shortDescription ?? undefined,
    link: payloadVoice.link ?? undefined,
    previewUrl: payloadVoice.previewUrl ?? undefined,
  };
}

/**
 * Balances subNavGroups for side-by-side display by merging adjacent groups
 * when doing so reduces the visual imbalance between column heights.
 *
 * Returns an array of columns, where each column is an array of ISubNav groups
 * to stack vertically. Adjacent groups may be combined into the same column to
 * achieve a more even distribution of link counts across columns.
 *
 * Never collapses all groups into a single column.
 *
 * @example
 * // [5 links, 2 links, 1 link] → [[5-link group], [2-link group, 1-link group]]
 * balanceSubNavs(groups) // two columns of heights [5, 3] instead of [5, 2, 1]
 */
export function balanceSubNavs(groups: ISubNav[]): ISubNav[][] {
  if (groups.length <= 1) return [groups];

  const columnHeight = (col: ISubNav[]) => col.reduce((sum, g) => sum + (g.items?.length ?? 0), 0);

  const columnRange = (cols: ISubNav[][]) => {
    const heights = cols.map(columnHeight);
    return Math.max(...heights) - Math.min(...heights);
  };

  const n = groups.length;
  let bestScore = Infinity;
  let bestColumns: ISubNav[][] = groups.map(g => [g]);

  // Enumerate all contiguous partitions into ≥2 columns (2^(n−1) combinations).
  // Bit i of the mask indicates a column split before groups[i+1].
  for (let mask = 1; mask < 1 << (n - 1); mask++) {
    const columns: ISubNav[][] = [];
    let current: ISubNav[] = [groups[0]];

    for (let i = 1; i < n; i++) {
      if (mask & (1 << (i - 1))) {
        columns.push(current);
        current = [groups[i]];
      } else {
        current.push(groups[i]);
      }
    }
    columns.push(current);

    // Primary: minimize range; secondary: prefer more columns (fewer merges).
    const score = columnRange(columns) * 1000 - columns.length;
    if (score < bestScore) {
      bestScore = score;
      bestColumns = columns;
    }
  }

  return bestColumns;
}

/** Resolves a CMS voice field (voice ID string or populated Voice entity) to a voice ID string. */
export function getVoiceId(voice: unknown): string | undefined {
  if (typeof voice === "string") {
    return voice;
  }
  if (typeof voice === "object" && voice !== null && "voiceId" in voice) {
    const voiceId = (voice as { voiceId: unknown }).voiceId;
    if (typeof voiceId === "string") {
      return voiceId;
    }
  }
  return undefined;
}

export {
  formatLinks,
  formatNavLinks,
  formatComplexTextField,
  isEntity,
  isPublished,
  programaticNarrativesToNarrative,
  programmaticFaqsToFaqs,
};
